import { useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, GitBranch, Pencil, Plus, RefreshCw, Save, Waypoints } from 'lucide-react';
import { toast } from 'sonner';

import { AccountLedgerDialog } from '../../components/financials/AccountLedgerDialog';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { cn } from '../../lib/utils';
import { api } from '../../services/api';
import type {
    Account,
    AccountRemapPreview,
    AccountUpsertPayload,
    BalanceSheetStatementGroup,
    ProfitAndLossStatementGroup,
    VATAccountSettings,
} from '../../services/types';

type AccountViewMode = 'tree' | 'list';
type AccountStatusFilter = 'active' | 'inactive' | 'all';

const ACCOUNT_TYPE_OPTIONS = ['Asset', 'Liability', 'Equity', 'Revenue', 'Income', 'Expense'];

const PROFIT_AND_LOSS_GROUP_OPTIONS: Array<{ value: ProfitAndLossStatementGroup | ''; label: string }> = [
    { value: '', label: 'Unclassified' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'cogs', label: 'Cost of Goods Sold' },
    { value: 'admin_expense', label: 'Administrative Expense' },
    { value: 'selling_expense', label: 'Selling Expense' },
    { value: 'finance_cost', label: 'Finance Cost' },
    { value: 'other_income', label: 'Other Income' },
    { value: 'other_expense', label: 'Other Expense' },
    { value: 'exclude', label: 'Exclude from statement' },
];

const BALANCE_SHEET_GROUP_OPTIONS: Array<{ value: BalanceSheetStatementGroup | ''; label: string }> = [
    { value: '', label: 'Unclassified' },
    { value: 'current_asset', label: 'Current Asset' },
    { value: 'noncurrent_asset', label: 'Non-Current Asset' },
    { value: 'contra_asset', label: 'Contra Asset' },
    { value: 'current_liability', label: 'Current Liability' },
    { value: 'noncurrent_liability', label: 'Non-Current Liability' },
    { value: 'equity_capital', label: 'Equity - Capital' },
    { value: 'equity_retained_earnings', label: 'Equity - Retained Earnings' },
    { value: 'equity_current_year_profit', label: 'Equity - Current Year Profit' },
    { value: 'exclude', label: 'Exclude from statement' },
];

const EMPTY_DRAFT: AccountUpsertPayload = {
    code: '',
    name: '',
    type: 'Asset',
    parent_account_id: null,
    is_active: true,
    account_pnl_group: null,
    account_bs_group: null,
};

function formatStatementGroup(value?: string | null) {
    if (!value) return 'Unclassified';
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (token) => token.toUpperCase());
}

function formatAmount(value?: number | null) {
    return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isRemapSource(account: Account) {
    return account.is_active !== false && account.has_children === true;
}

function matchesStatus(account: Account, status: AccountStatusFilter) {
    const isActive = account.is_active !== false;
    if (status === 'all') return true;
    return status === 'active' ? isActive : !isActive;
}

function matchesType(account: Account, type: string) {
    return type === 'all' || account.type === type;
}

function buildTreeRows(accounts: Account[]) {
    const sorted = [...accounts].sort((left, right) => {
        const codeCompare = String(left.code || '').localeCompare(String(right.code || ''), undefined, { numeric: true });
        if (codeCompare !== 0) return codeCompare;
        return String(left.name || '').localeCompare(String(right.name || ''));
    });
    const visibleIds = new Set(sorted.map((account) => account.id));
    const childrenByParent = new Map<number | null, Account[]>();

    sorted.forEach((account) => {
        const parentKey = account.parent_account_id && visibleIds.has(account.parent_account_id)
            ? account.parent_account_id
            : null;
        const bucket = childrenByParent.get(parentKey) || [];
        bucket.push(account);
        childrenByParent.set(parentKey, bucket);
    });

    const flattened: Array<{ account: Account; depth: number }> = [];
    const visited = new Set<number>();

    const visit = (parentId: number | null, depth: number) => {
        (childrenByParent.get(parentId) || []).forEach((account) => {
            if (visited.has(account.id)) return;
            visited.add(account.id);
            flattened.push({ account, depth });
            visit(account.id, depth + 1);
        });
    };

    visit(null, 0);

    sorted.forEach((account) => {
        if (!visited.has(account.id)) {
            flattened.push({ account, depth: 0 });
        }
    });

    return flattened;
}

function toAccountDraft(account: Account): AccountUpsertPayload {
    return {
        code: account.code || '',
        name: account.name || '',
        type: account.type || 'Asset',
        parent_account_id: account.parent_account_id ?? null,
        is_active: account.is_active !== false,
        account_pnl_group: account.account_pnl_group || null,
        account_bs_group: account.account_bs_group || null,
    };
}

export function ChartOfAccounts() {
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [vatSaving, setVatSaving] = useState(false);
    const [vatSettings, setVatSettings] = useState<VATAccountSettings>({
        input_vat_account_id: 1202,
        output_vat_account_id: 2002,
    });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
    const [draft, setDraft] = useState<AccountUpsertPayload>(EMPTY_DRAFT);
    const [ledgerAccount, setLedgerAccount] = useState<Pick<Account, 'id' | 'code' | 'name' | 'type'> | null>(null);
    const [ledgerOpen, setLedgerOpen] = useState(false);
    const [remapOpen, setRemapOpen] = useState(false);
    const [remapAccount, setRemapAccount] = useState<Account | null>(null);
    const [remapPreview, setRemapPreview] = useState<AccountRemapPreview | null>(null);
    const [remapLoading, setRemapLoading] = useState(false);
    const [remapSaving, setRemapSaving] = useState(false);
    const [remapFrom, setRemapFrom] = useState('');
    const [remapTo, setRemapTo] = useState('');
    const [remapTargetId, setRemapTargetId] = useState('');
    const [remapReason, setRemapReason] = useState('');
    const [selectedRemapLineIds, setSelectedRemapLineIds] = useState<Set<number>>(new Set());
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        type: 'all',
        status: 'active',
        view: 'tree',
    });

    const loadAccounts = async () => {
        try {
            setLoading(true);
            const payload = await api.financials.listAccounts();
            setAccounts(payload);
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load chart of accounts');
        } finally {
            setLoading(false);
        }
    };

    const loadVatSettings = async () => {
        try {
            setVatSettings(await api.financials.getVATAccountSettings());
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load VAT account mappings');
        }
    };

    useEffect(() => {
        void loadAccounts();
        void loadVatSettings();
    }, []);

    const saveVatSettings = async () => {
        try {
            setVatSaving(true);
            setVatSettings(await api.financials.updateVATAccountSettings(vatSettings));
            toast.success('VAT account mappings updated');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to update VAT account mappings');
        } finally {
            setVatSaving(false);
        }
    };

    const filteredAccounts = useMemo(() => {
        const searchTerm = state.q.trim().toLowerCase();
        return accounts.filter((account) => {
            const haystack = [
                account.code,
                account.name,
                account.type,
                account.account_pnl_group,
                account.account_bs_group,
                account.is_active === false ? 'inactive' : 'active',
            ]
                .join(' ')
                .toLowerCase();
            return matchesType(account, state.type) && matchesStatus(account, state.status as AccountStatusFilter) && (!searchTerm || haystack.includes(searchTerm));
        });
    }, [accounts, state.q, state.status, state.type]);

    const displayRows = useMemo(() => {
        const mode = state.view as AccountViewMode;
        if (mode === 'list') {
            return [...filteredAccounts]
                .sort((left, right) => String(left.code || '').localeCompare(String(right.code || ''), undefined, { numeric: true }))
                .map((account) => ({ account, depth: 0 }));
        }
        return buildTreeRows(filteredAccounts);
    }, [filteredAccounts, state.view]);

    const sortedAccounts = useMemo(
        () =>
            [...accounts].sort((left, right) => String(left.code || '').localeCompare(String(right.code || ''), undefined, { numeric: true })),
        [accounts]
    );

    const activeAccounts = useMemo(
        () => accounts.filter((account) => account.is_active !== false).length,
        [accounts]
    );

    const openCreateDialog = () => {
        setEditingAccountId(null);
        setDraft(EMPTY_DRAFT);
        setDialogOpen(true);
    };

    const openEditDialog = async (account: Account) => {
        setEditingAccountId(account.id);
        setDialogOpen(true);
        setDraft(toAccountDraft(account));
        try {
            const latest = await api.financials.getAccount(account.id);
            setDraft(toAccountDraft(latest));
        } catch (error: any) {
            toast.error(error?.message || 'Loaded existing account details from the current list');
        }
    };

    const handleDialogChange = (open: boolean) => {
        setDialogOpen(open);
        if (!open) {
            setEditingAccountId(null);
            setDraft(EMPTY_DRAFT);
        }
    };

    const handleDraftChange = <K extends keyof AccountUpsertPayload>(key: K, value: AccountUpsertPayload[K]) => {
        setDraft((current) => ({
            ...current,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!draft.code.trim() || !draft.name.trim() || !draft.type.trim()) {
            toast.error('Code, name, and type are required');
            return;
        }

        const payload: AccountUpsertPayload = {
            code: draft.code.trim(),
            name: draft.name.trim(),
            type: draft.type,
            parent_account_id: draft.parent_account_id || null,
            is_active: draft.is_active !== false,
            account_pnl_group: draft.account_pnl_group || null,
            account_bs_group: draft.account_bs_group || null,
        };

        try {
            setSaving(true);
            if (editingAccountId) {
                await api.financials.updateAccount(editingAccountId, payload);
                toast.success('Account updated');
            } else {
                await api.financials.createAccount(payload);
                toast.success('Account created');
            }
            handleDialogChange(false);
            await loadAccounts();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to save account');
        } finally {
            setSaving(false);
        }
    };

    const openLedger = (account: Account) => {
        setLedgerAccount({
            id: account.id,
            code: account.code,
            name: account.name,
            type: account.type,
        });
        setLedgerOpen(true);
    };

    const loadRemapPreview = async (account: Account, filters?: { from?: string; to?: string }) => {
        try {
            setRemapLoading(true);
            const payload = await api.financials.getAccountRemapPreview(account.id, {
                from: filters?.from || undefined,
                to: filters?.to || undefined,
                limit: 500,
                offset: 0,
            });
            setRemapPreview(payload);
            setSelectedRemapLineIds(new Set());
            const firstTarget = payload.eligible_targets?.[0];
            setRemapTargetId(firstTarget ? String(firstTarget.id) : '');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to load remap preview');
        } finally {
            setRemapLoading(false);
        }
    };

    const openRemapDialog = async (account: Account) => {
        setRemapAccount(account);
        setRemapPreview(null);
        setRemapFrom('');
        setRemapTo('');
        setRemapReason('');
        setSelectedRemapLineIds(new Set());
        setRemapOpen(true);
        await loadRemapPreview(account);
    };

    const handleRemapOpenChange = (open: boolean) => {
        setRemapOpen(open);
        if (!open) {
            setRemapAccount(null);
            setRemapPreview(null);
            setSelectedRemapLineIds(new Set());
            setRemapTargetId('');
            setRemapReason('');
        }
    };

    const toggleRemapLine = (lineId: number) => {
        setSelectedRemapLineIds((current) => {
            const next = new Set(current);
            if (next.has(lineId)) {
                next.delete(lineId);
            } else {
                next.add(lineId);
            }
            return next;
        });
    };

    const toggleAllRemapLines = () => {
        const ids = (remapPreview?.items || []).map((item) => item.line_id).filter(Boolean);
        setSelectedRemapLineIds((current) => (ids.length > 0 && current.size === ids.length ? new Set() : new Set(ids)));
    };

    const submitRemap = async () => {
        if (!remapAccount) return;
        if (!remapTargetId) {
            toast.error('Select the target child account');
            return;
        }
        if (selectedRemapLineIds.size === 0) {
            toast.error('Select at least one journal line to remap');
            return;
        }
        if (!remapReason.trim()) {
            toast.error('Enter a reason for the audit trail');
            return;
        }
        try {
            setRemapSaving(true);
            const result = await api.financials.remapAccountLines(remapAccount.id, {
                target_account_id: Number(remapTargetId),
                line_ids: Array.from(selectedRemapLineIds),
                reason: remapReason.trim(),
            });
            toast.success(`Remapped ${result.line_count} journal line(s)`);
            await loadAccounts();
            await loadRemapPreview(remapAccount, { from: remapFrom, to: remapTo });
            setRemapReason('');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to remap journal lines');
        } finally {
            setRemapSaving(false);
        }
    };

    const dialogTitle = editingAccountId ? 'Edit Account' : 'Create Account';
    const dialogDescription = editingAccountId
        ? 'Update the account profile, hierarchy, and statement classification.'
        : 'Add a new account to the chart of accounts and classify it for statement presentation.';
    const selectedRemapRows = (remapPreview?.items || []).filter((item) => selectedRemapLineIds.has(item.line_id));
    const selectedRemapDebit = selectedRemapRows.reduce((sum, item) => sum + Number(item.debit || 0), 0);
    const selectedRemapCredit = selectedRemapRows.reduce((sum, item) => sum + Number(item.credit || 0), 0);

    return (
        <div className="space-y-4" data-testid="coa-panel">
            <Card>
                <CardHeader className="pb-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                            <CardTitle>Chart of Accounts</CardTitle>
                            <CardDescription>
                                Manage account structure, parent-child hierarchy, active status, and Profit &amp; Loss / Balance Sheet presentation groups.
                            </CardDescription>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button type="button" variant="outline" onClick={() => void loadAccounts()} disabled={loading}>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Refresh
                            </Button>
                            <Button type="button" onClick={openCreateDialog}>
                                <Plus className="mr-2 h-4 w-4" />
                                New Account
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-sm font-medium text-slate-500">Accounts Loaded</div>
                        <div className="mt-2 text-3xl font-bold text-slate-900">{accounts.length}</div>
                        <div className="mt-1 text-xs text-slate-500">Full COA list from Financials</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-sm font-medium text-slate-500">Active Accounts</div>
                        <div className="mt-2 text-3xl font-bold text-slate-900">{activeAccounts}</div>
                        <div className="mt-1 text-xs text-slate-500">Inactive accounts stay visible for audit traceability</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-sm font-medium text-slate-500">Statement Ownership</div>
                        <div className="mt-2 text-lg font-semibold text-slate-900">COA now owns grouping</div>
                        <div className="mt-1 text-xs text-slate-500">Use the classification fields here instead of Reporting Setup for statement presentation.</div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <CardTitle>VAT Posting Accounts</CardTitle>
                            <CardDescription>Finance-controlled accounts used when sales invoices and supplier bills are posted.</CardDescription>
                        </div>
                        <Button type="button" onClick={() => void saveVatSettings()} disabled={vatSaving || loading}>
                            <Save className="mr-2 h-4 w-4" />
                            {vatSaving ? 'Saving...' : 'Save Mappings'}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700">Input VAT Recoverable</span>
                        <select
                            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                            value={vatSettings.input_vat_account_id}
                            onChange={(event) => setVatSettings((current) => ({ ...current, input_vat_account_id: Number(event.target.value) }))}
                        >
                            {accounts.filter((account) => account.type === 'Asset' && account.is_active !== false && account.has_children !== true).map((account) => (
                                <option key={account.id} value={account.id}>{account.code} - {account.name}</option>
                            ))}
                        </select>
                    </label>
                    <label className="space-y-2 text-sm">
                        <span className="font-medium text-slate-700">Output VAT</span>
                        <select
                            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                            value={vatSettings.output_vat_account_id}
                            onChange={(event) => setVatSettings((current) => ({ ...current, output_vat_account_id: Number(event.target.value) }))}
                        >
                            {accounts.filter((account) => account.type === 'Liability' && account.is_active !== false && account.has_children !== true).map((account) => (
                                <option key={account.id} value={account.id}>{account.code} - {account.name}</option>
                            ))}
                        </select>
                    </label>
                </CardContent>
            </Card>

            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search code, account name, type, or statement group..."
                controls={[
                    {
                        key: 'type',
                        label: 'Type',
                        type: 'select',
                        value: state.type,
                        onChange: (value) => setFilter('type', value),
                        options: [
                            { value: 'all', label: 'All Types' },
                            ...ACCOUNT_TYPE_OPTIONS.map((option) => ({ value: option, label: option })),
                        ],
                    },
                    {
                        key: 'status',
                        label: 'Status',
                        type: 'select',
                        value: state.status,
                        onChange: (value) => setFilter('status', value),
                        options: [
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' },
                            { value: 'all', label: 'All Statuses' },
                        ],
                    },
                    {
                        key: 'view',
                        label: 'View',
                        type: 'chips',
                        value: state.view,
                        onChange: (value) => setFilter('view', value),
                        options: [
                            { value: 'tree', label: 'Tree' },
                            { value: 'list', label: 'List' },
                        ],
                    },
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={<span>{state.view === 'tree' ? 'Hierarchy view using parent accounts' : 'Flat account list view'}</span>}
            />

            <Card>
                <CardContent className="pt-6">
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                        <table className="w-full min-w-[1100px] text-left text-sm">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-slate-500">Code</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Account</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Profit &amp; Loss Group</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Balance Sheet Group</th>
                                    <th className="px-4 py-3 font-medium text-slate-500">Status</th>
                                    <th className="sticky right-0 z-10 min-w-[220px] bg-slate-50 px-4 py-3 text-right font-medium text-slate-500 shadow-[-8px_0_16px_-16px_rgba(15,23,42,0.35)]">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                                            Loading chart of accounts...
                                        </td>
                                    </tr>
                                ) : displayRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                                            No accounts match the current filters.
                                        </td>
                                    </tr>
                                ) : (
                                    displayRows.map(({ account, depth }) => (
                                        <tr key={account.id} className="border-b border-slate-100 last:border-b-0">
                                            <td className="px-4 py-3 font-medium text-slate-900">{account.code || '-'}</td>
                                            <td className="px-4 py-3">
                                                <div
                                                    className="flex items-center gap-2"
                                                    style={{ paddingLeft: state.view === 'tree' ? `${depth * 20}px` : undefined }}
                                                >
                                                    {state.view === 'tree' ? (
                                                        depth > 0 ? <Waypoints className="h-4 w-4 text-slate-400" /> : <GitBranch className="h-4 w-4 text-slate-400" />
                                                    ) : null}
                                                    <div className="min-w-0">
                                                        <div className={cn('font-medium text-slate-900', account.is_active === false && 'text-slate-500 line-through')}>
                                                            {account.name}
                                                        </div>
                                                        <div className="text-xs text-slate-500">
                                                            Parent: {account.parent_account_id ? `#${account.parent_account_id}` : 'Top level'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">{account.type || '-'}</td>
                                            <td className="px-4 py-3 text-slate-600">{formatStatementGroup(account.account_pnl_group)}</td>
                                            <td className="px-4 py-3 text-slate-600">{formatStatementGroup(account.account_bs_group)}</td>
                                            <td className="px-4 py-3">
                                                <span
                                                    className={cn(
                                                        'inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                                                        account.is_active === false
                                                            ? 'border-slate-200 bg-slate-100 text-slate-500'
                                                            : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                                    )}
                                                >
                                                    {account.is_active === false ? 'Inactive' : 'Active'}
                                                </span>
                                            </td>
                                            <td className="sticky right-0 z-10 bg-white px-4 py-3 shadow-[-8px_0_16px_-16px_rgba(15,23,42,0.35)]">
                                                <div className="flex justify-end gap-2">
                                                    {isRemapSource(account) && (
                                                        <Button type="button" variant="outline" size="sm" onClick={() => void openRemapDialog(account)}>
                                                            <ArrowRightLeft className="mr-2 h-4 w-4" />
                                                            Remap Entries
                                                        </Button>
                                                    )}
                                                    <Button type="button" variant="outline" size="sm" onClick={() => void openEditDialog(account)}>
                                                        <Pencil className="mr-2 h-4 w-4" />
                                                        Edit
                                                    </Button>
                                                    <Button type="button" variant="outline" size="sm" onClick={() => openLedger(account)}>
                                                        View Ledger
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                        <DialogDescription>{dialogDescription}</DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Account Code</span>
                            <Input
                                value={draft.code}
                                onChange={(event) => handleDraftChange('code', event.target.value)}
                                placeholder="e.g. 4000"
                            />
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Account Name</span>
                            <Input
                                value={draft.name}
                                onChange={(event) => handleDraftChange('name', event.target.value)}
                                placeholder="e.g. Sales Revenue"
                            />
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Account Type</span>
                            <select
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                value={draft.type}
                                onChange={(event) => handleDraftChange('type', event.target.value)}
                            >
                                {ACCOUNT_TYPE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Parent Account</span>
                            <select
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                value={draft.parent_account_id ? String(draft.parent_account_id) : ''}
                                onChange={(event) => handleDraftChange('parent_account_id', event.target.value ? Number(event.target.value) : null)}
                            >
                                <option value="">Top level</option>
                                {sortedAccounts
                                    .filter((account) => account.id !== editingAccountId)
                                    .map((account) => (
                                        <option key={account.id} value={String(account.id)}>
                                            {account.code} · {account.name}
                                        </option>
                                    ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Profit &amp; Loss Group</span>
                            <select
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                value={draft.account_pnl_group || ''}
                                onChange={(event) => handleDraftChange('account_pnl_group', event.target.value || null)}
                            >
                                {PROFIT_AND_LOSS_GROUP_OPTIONS.map((option) => (
                                    <option key={option.value || 'empty'} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm">
                            <span className="font-medium text-slate-700">Balance Sheet Group</span>
                            <select
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                value={draft.account_bs_group || ''}
                                onChange={(event) => handleDraftChange('account_bs_group', event.target.value || null)}
                            >
                                {BALANCE_SHEET_GROUP_OPTIONS.map((option) => (
                                    <option key={option.value || 'empty'} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="space-y-2 text-sm md:col-span-2">
                            <span className="font-medium text-slate-700">Status</span>
                            <select
                                className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                value={draft.is_active === false ? 'inactive' : 'active'}
                                onChange={(event) => handleDraftChange('is_active', event.target.value === 'active')}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </label>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                        Statement presentation is maintained here so Profit &amp; Loss and Balance Sheet groupings stay aligned with the live chart of accounts.
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => handleDialogChange(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={() => void handleSave()} disabled={saving}>
                            {saving ? 'Saving...' : editingAccountId ? 'Save Changes' : 'Create Account'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={remapOpen} onOpenChange={handleRemapOpenChange}>
                <DialogContent className="flex max-h-[calc(100vh-2rem)] max-w-6xl flex-col gap-0 overflow-hidden p-0">
                    <DialogHeader className="shrink-0 border-b border-slate-200 px-6 py-4 pr-12">
                        <DialogTitle>Remap Parent Entries</DialogTitle>
                        <DialogDescription>
                            Move selected posted journal lines from a parent summary account to one of its child posting accounts.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
                        <div className="grid gap-3 md:grid-cols-4">
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-xs uppercase text-slate-500">Source</div>
                                <div className="mt-1 font-medium text-slate-900">
                                    {remapAccount ? `${remapAccount.code} - ${remapAccount.name}` : '-'}
                                </div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-xs uppercase text-slate-500">Lines Loaded</div>
                                <div className="mt-1 font-medium text-slate-900">{remapPreview?.total || 0}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-xs uppercase text-slate-500">Total Debit</div>
                                <div className="mt-1 font-medium text-slate-900">{formatAmount(remapPreview?.total_debit)}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-xs uppercase text-slate-500">Total Credit</div>
                                <div className="mt-1 font-medium text-slate-900">{formatAmount(remapPreview?.total_credit)}</div>
                            </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                            <label className="space-y-2 text-sm">
                                <span className="font-medium text-slate-700">From Date</span>
                                <Input type="date" value={remapFrom} onChange={(event) => setRemapFrom(event.target.value)} />
                            </label>
                            <label className="space-y-2 text-sm">
                                <span className="font-medium text-slate-700">To Date</span>
                                <Input type="date" value={remapTo} onChange={(event) => setRemapTo(event.target.value)} />
                            </label>
                            <div className="flex items-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => remapAccount && void loadRemapPreview(remapAccount, { from: remapFrom, to: remapTo })}
                                    disabled={remapLoading || !remapAccount}
                                >
                                    {remapLoading ? 'Loading...' : 'Apply Filter'}
                                </Button>
                            </div>
                        </div>

                        <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
                            <label className="space-y-2 text-sm">
                                <span className="font-medium text-slate-700">Target Child Account</span>
                                <select
                                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                                    value={remapTargetId}
                                    onChange={(event) => setRemapTargetId(event.target.value)}
                                >
                                    <option value="">Select target account</option>
                                    {(remapPreview?.eligible_targets || []).map((account) => (
                                        <option key={account.id} value={account.id}>
                                            {account.code} - {account.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="space-y-2 text-sm">
                                <span className="font-medium text-slate-700">Reason / Audit Remarks</span>
                                <Input
                                    value={remapReason}
                                    onChange={(event) => setRemapReason(event.target.value)}
                                    placeholder="e.g. Finance expert pre-go-live parent-to-child account correction"
                                />
                            </label>
                        </div>

                        <div className="rounded-lg border border-slate-200">
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                                <div>
                                    Selected {selectedRemapLineIds.size} line(s) · Debit {formatAmount(selectedRemapDebit)} · Credit {formatAmount(selectedRemapCredit)}
                                </div>
                                <Button type="button" size="sm" variant="outline" onClick={toggleAllRemapLines} disabled={(remapPreview?.items || []).length === 0}>
                                    {selectedRemapLineIds.size === (remapPreview?.items || []).length && selectedRemapLineIds.size > 0 ? 'Clear Selection' : 'Select Loaded Lines'}
                                </Button>
                            </div>
                            <div className="max-h-[min(38vh,360px)] overflow-auto">
                                <table className="w-full min-w-[900px] text-left text-sm">
                                    <thead className="sticky top-0 bg-white shadow-sm">
                                        <tr>
                                            <th className="px-3 py-2 font-medium text-slate-500">Select</th>
                                            <th className="px-3 py-2 font-medium text-slate-500">Date</th>
                                            <th className="px-3 py-2 font-medium text-slate-500">Journal</th>
                                            <th className="px-3 py-2 font-medium text-slate-500">Description</th>
                                            <th className="px-3 py-2 text-right font-medium text-slate-500">Debit</th>
                                            <th className="px-3 py-2 text-right font-medium text-slate-500">Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {remapLoading ? (
                                            <tr>
                                                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">Loading remap preview...</td>
                                            </tr>
                                        ) : (remapPreview?.items || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">No journal lines match this source account and filter.</td>
                                            </tr>
                                        ) : (
                                            (remapPreview?.items || []).map((line) => (
                                                <tr key={line.line_id} className="border-t border-slate-100">
                                                    <td className="px-3 py-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRemapLineIds.has(line.line_id)}
                                                            onChange={() => toggleRemapLine(line.line_id)}
                                                        />
                                                    </td>
                                                    <td className="px-3 py-2 text-slate-600">{line.journal_date || '-'}</td>
                                                    <td className="px-3 py-2 font-medium text-slate-900">{line.journal_no || `#${line.journal_id}`}</td>
                                                    <td className="px-3 py-2 text-slate-600">{line.description || '-'}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{formatAmount(line.debit)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{formatAmount(line.credit)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="rounded-lg border border-slate-200">
                            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">Recent Remap History</div>
                            <div className="max-h-40 overflow-auto">
                                {(remapPreview?.history || []).length === 0 ? (
                                    <div className="px-3 py-6 text-sm text-slate-500">No remap batches recorded yet.</div>
                                ) : (
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-100">
                                                <th className="px-3 py-2 font-medium text-slate-500">Batch</th>
                                                <th className="px-3 py-2 font-medium text-slate-500">Target</th>
                                                <th className="px-3 py-2 font-medium text-slate-500">Lines</th>
                                                <th className="px-3 py-2 font-medium text-slate-500">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(remapPreview?.history || []).map((item) => (
                                                <tr key={item.remap_id} className="border-b border-slate-100 last:border-b-0">
                                                    <td className="px-3 py-2">#{item.remap_id}</td>
                                                    <td className="px-3 py-2">{item.target_code} - {item.target_name}</td>
                                                    <td className="px-3 py-2">{item.line_count}</td>
                                                    <td className="px-3 py-2">{item.reason}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 -mx-6 -mb-4 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4 shadow-[0_-10px_20px_-20px_rgba(15,23,42,0.45)]">
                            <Button type="button" variant="outline" onClick={() => handleRemapOpenChange(false)} disabled={remapSaving}>
                                Close
                            </Button>
                            <Button type="button" onClick={() => void submitRemap()} disabled={remapSaving || remapLoading}>
                                {remapSaving ? 'Remapping...' : 'Confirm Remap'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <AccountLedgerDialog
                open={ledgerOpen}
                onOpenChange={setLedgerOpen}
                account={ledgerAccount}
            />
        </div>
    );
}
